import { useMemo } from 'react'
import { Dayjs } from 'dayjs'
import { useEmployeesQuery } from '@/features/employee-management/hooks/useEmployeesQuery'
import { useScheduleStore } from '../store/useScheduleStore'
import { RosterRow, Shift } from '../types/schedule.model'
import { toDateKey, weekDays } from '../utils/schedule'
import { colorForEmployee } from '../utils/colors'

/**
 * Joins the employee list (real, from the API) with the roster (local, from the
 * store) and hands components ready-made rows. When the backend grows a shift
 * endpoint, only the `shifts` source below changes.
 */
export const useSchedule = (anchorDay: Dayjs) => {
  const { employeesList, employeesListLoading } = useEmployeesQuery()
  const shifts = useScheduleStore(state => state.shifts)
  const colorsByUserId = useScheduleStore(state => state.colorsByUserId)

  const days = useMemo(() => weekDays(anchorDay), [anchorDay])

  const employees = useMemo(
    () =>
      (employeesList ?? []).map((employee, index) => ({
        userId: employee.userId,
        // Display names live in the identity provider and can be missing -
        // same fallback the device picker uses, so the roster never shows a
        // nameless row.
        name: employee.name || employee.email || employee.userId,
        // Position in the list is the fallback colour slot, so an employee
        // keeps the same colour until someone picks one explicitly.
        color: colorForEmployee(colorsByUserId, employee.userId, index)
      })),
    [employeesList, colorsByUserId]
  )

  const shiftsByUserAndDate = useMemo(() => {
    const map = new Map<string, Shift[]>()

    shifts.forEach(shift => {
      const key = `${shift.userId}|${shift.date}`
      const existing = map.get(key)
      if (existing) {
        existing.push(shift)
      } else {
        map.set(key, [shift])
      }
    })

    map.forEach(list => list.sort((a, b) => a.startMinutes - b.startMinutes))

    return map
  }, [shifts])

  const rows = useMemo<RosterRow[]>(
    () =>
      employees.map(employee => ({
        userId: employee.userId,
        name: employee.name,
        shiftsByDate: days.reduce<Record<string, Shift[]>>((acc, day) => {
          const dateKey = toDateKey(day)
          acc[dateKey] = shiftsByUserAndDate.get(`${employee.userId}|${dateKey}`) ?? []
          return acc
        }, {})
      })),
    [employees, days, shiftsByUserAndDate]
  )

  const shiftsForDay = useMemo(
    () => (day: Dayjs) => {
      const dateKey = toDateKey(day)
      return shifts
        .filter(shift => shift.date === dateKey)
        .sort((a, b) => a.startMinutes - b.startMinutes)
    },
    [shifts]
  )

  const employeeName = useMemo(
    () => (userId: string) =>
      employees.find(employee => employee.userId === userId)?.name ?? userId,
    [employees]
  )

  const employeeColor = useMemo(
    () => (userId: string) =>
      employees.find(employee => employee.userId === userId)?.color ??
      colorForEmployee(colorsByUserId, userId, 0),
    [employees, colorsByUserId]
  )

  return {
    days,
    rows,
    employees,
    employeeColor,
    employeesLoading: employeesListLoading,
    shiftsForDay,
    employeeName
  }
}
